/**
 * Folder-aware extraction of a drop's payload.
 *
 * `DataTransfer.files` only ever contains plain files — a dropped directory
 * shows up there as an unreadable zero-byte entry (or not at all). The
 * directory tree is reachable only through the non-standard-but-universal
 * `webkitGetAsEntry()` filesystem API on `DataTransfer.items`.
 *
 * Two hard constraints shape this module:
 *
 * 1. `webkitGetAsEntry()` must be called SYNCHRONOUSLY inside the drop event.
 *    The `items` list is cleared as soon as the event handler yields, so we
 *    snapshot every entry first and only then start awaiting.
 * 2. `FileSystemDirectoryReader.readEntries()` returns at most ~100 entries per
 *    call and signals "done" with an empty array — it has to be called in a
 *    loop until that empty batch arrives.
 */

/** Directories that are never worth attaching to a message. */
const SKIPPED_DIRECTORIES = new Set([
	'node_modules',
	'.git',
	'.next',
	'dist',
	'build',
	'out',
	'coverage',
	'__pycache__',
	'.venv',
	'venv',
	'target',
	'vendor',
	'DerivedData',
]);

/** Files that are OS/browser bookkeeping rather than user content. */
const SKIPPED_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);
const SKIPPED_EXTENSIONS = ['.crdownload', '.tmp'];

/** Hard ceiling on how many files one drop may yield, whatever the tree size. */
export const MAX_DROPPED_FILES = 500;

export type DroppedFile = {
	file: File;
	/** Path inside the dropped folder, e.g. `api-docs/spec/openapi.md`. */
	relativePath: string;
};

export type DroppedPayload = {
	files: DroppedFile[];
	/** True when at least one dropped item was a directory. */
	hadFolder: boolean;
	/** Top-level names of the dropped directories, in drop order. */
	folderNames: string[];
	/** True when traversal stopped at `MAX_DROPPED_FILES`. */
	truncated: boolean;
};

const isSkippedFile = (name: string) =>
	SKIPPED_FILES.has(name) ||
	SKIPPED_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

/**
 * Records the path on the File itself so downstream pipeline work can preserve
 * folder structure without threading a parallel array through every call site.
 * Non-enumerable so it never leaks into spreads, JSON, or FormData building.
 */
const tagRelativePath = (file: File, relativePath: string): File => {
	try {
		Object.defineProperty(file, 'naumuRelativePath', {
			value: relativePath,
			enumerable: false,
			configurable: true,
		});
	} catch {
		// Some browsers seal File instances; the path is still returned alongside.
	}
	return file;
};

const readFile = (entry: FileSystemFileEntry): Promise<File | null> =>
	new Promise((resolve) => {
		entry.file(
			(file) => resolve(file),
			() => resolve(null),
		);
	});

const readBatch = (
	reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> =>
	new Promise((resolve) => {
		reader.readEntries(
			(entries) => resolve(entries),
			() => resolve([]),
		);
	});

/** Drains a directory reader until it reports an empty batch. */
const readAllEntries = async (
	directory: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> => {
	const reader = directory.createReader();
	const all: FileSystemEntry[] = [];
	// Guard against a misbehaving reader that never returns an empty batch.
	for (let i = 0; i < 1000; i += 1) {
		const batch = await readBatch(reader);
		if (batch.length === 0) break;
		all.push(...batch);
	}
	return all;
};

type Collector = { files: DroppedFile[]; truncated: boolean };

const collectEntry = async (
	entry: FileSystemEntry,
	prefix: string,
	out: Collector,
): Promise<void> => {
	if (out.truncated) return;

	if (entry.isFile) {
		if (isSkippedFile(entry.name)) return;
		if (out.files.length >= MAX_DROPPED_FILES) {
			out.truncated = true;
			return;
		}
		const file = await readFile(entry as FileSystemFileEntry);
		if (!file) return;
		const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
		out.files.push({ file: tagRelativePath(file, relativePath), relativePath });
		return;
	}

	if (!entry.isDirectory) return;
	if (SKIPPED_DIRECTORIES.has(entry.name)) return;

	const childPrefix = prefix ? `${prefix}/${entry.name}` : entry.name;
	const children = await readAllEntries(entry as FileSystemDirectoryEntry);
	for (const child of children) {
		if (out.truncated) return;
		await collectEntry(child, childPrefix, out);
	}
};

const plainFilesPayload = (dataTransfer: DataTransfer): DroppedPayload => ({
	files: Array.from(dataTransfer.files).map((file) => ({
		file: tagRelativePath(file, file.name),
		relativePath: file.name,
	})),
	hadFolder: false,
	folderNames: [],
	truncated: false,
});

/**
 * Flattens a drop into a file list, walking any dropped directories.
 *
 * Falls back to `dataTransfer.files` whenever the filesystem API is missing or
 * the drop contained no directories, so plain-file drops behave exactly as
 * they did before folder support existed.
 */
export const extractDroppedFiles = async (
	dataTransfer: DataTransfer,
): Promise<DroppedPayload> => {
	const items = dataTransfer.items;
	if (!items || items.length === 0) return plainFilesPayload(dataTransfer);

	// Snapshot synchronously — `items` is emptied once this handler yields.
	const entries: FileSystemEntry[] = [];
	for (let i = 0; i < items.length; i += 1) {
		const item = items[i];
		if (item.kind !== 'file') continue;
		const entry =
			typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
		if (entry) entries.push(entry);
	}

	if (entries.length === 0) return plainFilesPayload(dataTransfer);

	const folderNames = entries.filter((e) => e.isDirectory).map((e) => e.name);
	if (folderNames.length === 0) return plainFilesPayload(dataTransfer);

	const out: Collector = { files: [], truncated: false };
	for (const entry of entries) {
		if (out.truncated) break;
		await collectEntry(entry, '', out);
	}

	return {
		files: out.files,
		hadFolder: true,
		folderNames,
		truncated: out.truncated,
	};
};
